import { Link, useMatchRoute, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  SidebarLanguageToggle,
  useSidebar,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  CalendarClock,
  BookOpen,
  ClipboardCheck,
  Users,
  MessageSquareText,
  Flag,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { trpc } from '@org/clients/school';

const navItems: Array<{ labelKey: string; href: string; icon: LucideIcon }> = [
  { labelKey: 'teacher.sidebar.navigation.dashboard', href: '/', icon: LayoutDashboard },
  { labelKey: 'teacher.sidebar.navigation.nextClasses', href: '/next-classes', icon: CalendarClock },
  { labelKey: 'teacher.sidebar.navigation.classes', href: '/classes', icon: BookOpen },
  { labelKey: 'teacher.sidebar.navigation.homework', href: '/homework', icon: ClipboardCheck },
  { labelKey: 'teacher.sidebar.navigation.students', href: '/students', icon: Users },
  { labelKey: 'teacher.sidebar.navigation.flags', href: '/flags', icon: Flag },
  { labelKey: 'teacher.sidebar.navigation.feedback', href: '/feedback', icon: MessageSquareText },
];

// Shown only to the pedagogical coordination (admins or the "coordination-panel" feature flag).
const coordinationItem = { labelKey: 'teacher.sidebar.navigation.coordination', href: '/coordination', icon: ShieldCheck };

export function AppSidebar() {
  const matchRoute = useMatchRoute();
  const routerState = useRouterState();
  const { isMobile, setOpen, setOpenMobile, toggleSidebar } = useSidebar();
  const { t } = useTranslation('admin');
  const { data: coordinationAccess } = trpc.teacher.coordination.access.useQuery(undefined, { staleTime: 5 * 60_000 });
  const items = coordinationAccess?.allowed ? [...navItems, coordinationItem] : navItems;

  const closeSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Sidebar className="border-r border-rose-100 bg-white/90" collapsible="offcanvas">
      <SidebarHeader className="flex flex-row items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={t('common.nav.menu')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#cf2e2e] shadow-sm"
          >
            <img src="/icon-hand-small-white.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
          </button>
          <span className="text-base font-semibold text-gray-900">English2Empower</span>
        </div>
        <SidebarTrigger className="text-[#cf2e2e]" />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('common.nav.menu')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const isActive =
                  matchRoute({ to: item.href, fuzzy: item.href === '/' }) &&
                  routerState.location.pathname.startsWith(item.href);
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={!!isActive} className="rounded-lg">
                      <Link to={item.href} onClick={closeSidebar}>
                        <Icon />
                        <span>{t(item.labelKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-3">
        <SidebarLanguageToggle />
      </SidebarFooter>
      <SidebarRail className="hidden md:flex" />
    </Sidebar>
  );
}